import unescapedText from 'I18n/utils/unescapedText';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { Text } from 'foundations-components/components/Text';
import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ArrowButtonWrapper = styled.div.withConfig({
  displayName: "EcosystemSlider__ArrowButtonWrapper"
})(["position:absolute;top:", ";", " opacity:", ";visibility:", ";transition:opacity 0.3s ease,visibility 0.3s ease;"], ({
  $top
}) => $top, ({
  $position
}) => $position === 'left' ? 'left: -26px;' : 'right: -26px;', ({
  $visible
}) => $visible ? '1' : '0', ({
  $visible
}) => $visible ? 'visible' : 'hidden');
const CARD_GAP = '400';
export function EcosystemSlider({
  children,
  cardWidth,
  description,
  gradientColor,
  title,
  onScrollButtonClick,
  scrollButtonTop = '40%',
  topRightSlot
}) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const theme = useTheme();
  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      const hasRightScroll = container.scrollLeft + container.clientWidth < container.scrollWidth;
      setCanScrollRight(hasRightScroll);
    }
  };
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // @ts-expect-error remove the scrollbar (for some reason, this property is not typed)
      container.style.scrollbarWidth = 'none';
      updateScrollButtons();
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, []);
  const getVisibleCardScrollAmount = () => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const cardGap = parseInt(theme.space[CARD_GAP], 10);
    const itemWidth = cardWidth + cardGap;
    const visibleCards = Math.floor(container.clientWidth / itemWidth);
    return itemWidth * Math.max(1, visibleCards);
  };
  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = getVisibleCardScrollAmount() * -1;
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      onScrollButtonClick === null || onScrollButtonClick === void 0 || onScrollButtonClick('left');
    }
  };
  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = getVisibleCardScrollAmount();
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      onScrollButtonClick === null || onScrollButtonClick === void 0 || onScrollButtonClick('right');
    }
  };
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      gap: "400",
      justifyContent: "space-between",
      alignItems: "end",
      children: [/*#__PURE__*/_jsxs(View, {
        children: [title && /*#__PURE__*/_jsx(Text, {
          as: "h3",
          marginBlockEnd: "200",
          children: title
        }), description && /*#__PURE__*/_jsx(Text, {
          color: "text-core-subtle",
          typography: "body-100",
          marginBlockEnd: "200",
          children: description
        })]
      }), /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        flexShrink: 0,
        children: topRightSlot
      })]
    }), /*#__PURE__*/_jsxs(View, {
      position: "relative",
      children: [/*#__PURE__*/_jsx(ScrollContainer, {
        gradientColor: gradientColor,
        marginInline: "-200",
        overflowY: "visible",
        scrollDirection: "horizontal",
        scrollElementRef: scrollContainerRef,
        children: /*#__PURE__*/_jsx(Flex, {
          gap: CARD_GAP,
          padding: "200",
          children: children.map((child, index) => /*#__PURE__*/_jsx(Flex, {
            alignItems: "stretch",
            flexShrink: 0,
            inlineSize: `${cardWidth}px`,
            children: child
          }, index))
        })
      }), /*#__PURE__*/_jsx(ArrowButtonWrapper, {
        $position: "left",
        $top: scrollButtonTop,
        $visible: canScrollLeft,
        children: /*#__PURE__*/_jsx(IconButton, {
          shape: "circle",
          onClick: handleScrollLeft,
          children: /*#__PURE__*/_jsx(LeftIcon, {
            "aria-label": unescapedText('trellisEcosystem.slider.scrollLeft')
          })
        })
      }), /*#__PURE__*/_jsx(ArrowButtonWrapper, {
        $position: "right",
        $top: scrollButtonTop,
        $visible: canScrollRight,
        children: /*#__PURE__*/_jsx(IconButton, {
          shape: "circle",
          onClick: handleScrollRight,
          children: /*#__PURE__*/_jsx(RightIcon, {
            "aria-label": unescapedText('trellisEcosystem.slider.scrollRight')
          })
        })
      })]
    })]
  });
}